import { EmptyState } from "@arcadeai/design-system/components/ui/molecules/empty-state";
import { McpSectionNavHeader } from "@arcadeai/design-system/components/ui/molecules/mcp-server-picker/section-nav-header";
import { McpServerAvatarStack } from "@arcadeai/design-system/components/ui/molecules/mcp-server-picker/server-avatar-stack";
import { plural, toSelectionChip } from "../mappers";
import { countSelectedTools } from "../selection";
import type { ToolGroup } from "../types";
import { ToolGroups } from "./tool-groups";

export function SelectedToolsReview({
  groups,
  onBack,
  onOpenServer,
  onToggleTool,
  scrollContainer = null,
  selected,
}: {
  groups: ToolGroup[];
  onBack: () => void;
  onOpenServer: (server: string) => void;
  onToggleTool: (fqn: string) => void;
  scrollContainer?: HTMLElement | null;
  selected: Set<string>;
}) {
  // Each group carries its server's full tool set, so the counts shown here
  // reflect the selection, not the catalog size.
  const countSelected = (group: ToolGroup) =>
    countSelectedTools(group.tools, selected);
  const toolCount = groups.reduce((n, group) => n + countSelected(group), 0);

  return (
    <section
      aria-label="Selected tools, grouped by MCP server"
      className="@container space-y-5"
    >
      <McpSectionNavHeader
        identity={
          <div className="flex min-w-0 items-center gap-2">
            <h3 className="truncate font-semibold text-sm">Selected tools</h3>
            <span className="shrink-0 text-muted-foreground text-xs tabular-nums">
              {plural(toolCount, "tool")} across{" "}
              {plural(groups.length, "server")}
            </span>
            <McpServerAvatarStack
              className="@md:inline-flex hidden rounded-full bg-muted/25 px-1 py-0.5 ring-1 ring-border/40"
              maxVisible={6}
              onOpenMcpServer={onOpenServer}
              overflowLabel="more selected MCP servers"
              servers={groups.map((group) =>
                toSelectionChip(group.server, countSelected(group))
              )}
            />
          </div>
        }
        onBack={onBack}
      />
      {groups.length > 0 ? (
        <ToolGroups
          groups={groups}
          onToggleTool={onToggleTool}
          scrollContainer={scrollContainer}
          selected={selected}
          showSelectedCount
        />
      ) : (
        <EmptyState
          description="Select tools from an MCP server and they'll appear here."
          role="status"
          title="No tools selected yet"
        />
      )}
    </section>
  );
}
